import React from "react";
import { Dialog, LongTextLabel } from "@sscs/ui";
import {
  CancelScheduleSendIcon as CancelReleaseIcon,
  RocketLaunchIcon,
} from "@sscs/ui/icons";
import {
  ActionMenu,
  ApprovalDialog,
  LinkToReport,
  Dialog as PortalDialog,
  ReanalyzeDialog,
  RevokeDialog,
} from "Components";
import { PERMISSION } from "Constants";
import { useDialog } from "Hooks";
import { DownloadDialog } from "Modules/download";
import { useUpdateVersionMutation } from "Services";
import { ActionItem, ActionType, ProcessingStatus, Version } from "Types";
import { canFileBeDownloaded, isApproved, isFileInProgress } from "Utils";
import { useApproveVersionMutation } from "../../services/projects.api";
import ReleaseDialog from "./ReleaseDialog/ReleaseDialog";
import useVersionActions from "./useVersionActions";
import styles from "./versionAction.module.scss";

type VersionAction = ActionType | "REVERT_RELEASE" | "RELEASE";

const shouldReanalyzeVersion = (
  processingStatus: ProcessingStatus,
  version: Version,
): boolean => {
  return (
    processingStatus.code === "ERROR" ||
    version.artifacts_summary.repro?.current_status.code === "ERROR"
  );
};

const generateDropdownActions = (
  version: Version,
  onClick: (action: VersionAction) => void,
): ActionItem[] => {
  const actions: ActionItem[] = [];
  const processingStatus = version.artifacts_summary.version.current_status;
  const isRepro = Boolean(
    version.artifacts_summary.repro?.latest_report_summary?.scan_report.final,
  );
  const showReanalyzeVersion = shouldReanalyzeVersion(
    processingStatus,
    version,
  );

  actions.push({
    id: "view-report",
    type: "VIEW_REPORT",
    text: (
      <LinkToReport
        id="view-report"
        type="text"
        report={{
          projectName: version.project_name,
          packageName: version.package_name,
          version: version.software_info.version,
          isRepro: isRepro,
        }}
      >
        View report
      </LinkToReport>
    ),
    isDisabled: processingStatus.code !== "DONE",
    onClick: () => onClick("VIEW_REPORT"),
  });

  if (version.is_released) {
    actions.push({
      id: "mark-as-unreleased",
      type: "CUSTOM",
      text: "Mark as unreleased",
      itemIcon: <CancelReleaseIcon />,
      onClick: () => onClick("REVERT_RELEASE"),
      permissions: [PERMISSION.EDIT_VERSION_SOFTWARE_INFO],
    });
  } else {
    actions.push({
      id: "mark-as-released",
      type: "CUSTOM",
      text: "Mark as released",
      itemIcon: <RocketLaunchIcon />,
      isDisabled: processingStatus.code !== "DONE",
      onClick: () => onClick("RELEASE"),
      permissions: [PERMISSION.EDIT_VERSION_SOFTWARE_INFO],
    });
  }

  actions.push({
    id: showReanalyzeVersion ? "reanalyze-version" : "reanalyze-package",
    type: "REANALYZE",
    text: showReanalyzeVersion ? "Reanalyze version" : "Reanalyze package",
    isDisabled: isFileInProgress(processingStatus),
    onClick: () => onClick("REANALYZE"),
    permissions: [PERMISSION.REANALYZE_VERSION, PERMISSION.REANALYZE_PACKAGE],
  });

  actions.push({
    id: "download",
    type: "DOWNLOAD",
    text: "Download file",
    isDisabled: !canFileBeDownloaded(processingStatus),
    onClick: () => onClick("DOWNLOAD"),
    permissions: [
      PERMISSION.MODIFY_VERSION_APPROVAL,
      PERMISSION.DOWNLOAD_VERSIONS,
    ],
  });

  actions.push({
    id: "delete-version",
    type: "DELETE",
    text: "Delete version",
    onClick: () => onClick("DELETE"),
    permissions: [PERMISSION.DELETE_VERSION],
  });

  if (
    version.approval_status === "Undetermined" ||
    version.approval_status === "ApprovalRejected"
  ) {
    actions.push({
      id: "approve-version",
      type: "APPROVAL",
      text: "Approve/Reject",
      isDisabled: processingStatus.code !== "DONE",
      onClick: () => onClick("APPROVAL"),
      permissions: [PERMISSION.MODIFY_VERSION_APPROVAL],
    });
  } else if (isApproved(version.approval_status)) {
    actions.push({
      id: "approve-version",
      type: "REVOKE_APPROVAL",
      text: "Revoke approval",
      isDisabled: processingStatus.code !== "DONE",
      onClick: () => onClick("REVOKE_APPROVAL"),
      permissions: [PERMISSION.MODIFY_VERSION_APPROVAL],
    });
  }

  return actions;
};

type VersionActionsProps = {
  id: string;
  version: Version;
  isDestructiveCatalogAction: boolean;
};

const VersionActions = ({
  id,
  version,
  isDestructiveCatalogAction,
}: VersionActionsProps): React.JSX.Element => {
  const approvalDialog = useDialog();
  const revokeDialog = useDialog();
  const downloadDialog = useDialog();
  const releaseDialog = useDialog();
  const reanalyzeDialog = useDialog();

  const { deleteVersion, reanalyzeVersion, revertRelease, deleteDialog } =
    useVersionActions(version);
  const [editApproval] = useApproveVersionMutation();
  const [editVersion] = useUpdateVersionMutation();

  const showReanalyzeVersion = shouldReanalyzeVersion(
    version.artifacts_summary.version.current_status,
    version,
  );

  /* TODO: Once we resolve issues with CSP and leaking of S3 bucket names
   * download will be done without users having to do an extra click.
   * For that reason, this code will remain commented out until we revert back to it.
   */
  // const { handleDownload } = getCloudFileDownloader(
  //   version.version_id,
  //   version.file_name,
  //   "VERSION"
  // );

  const handleMenuClick = async (action: VersionAction) => {
    if (action === "REANALYZE") {
      reanalyzeDialog.open();
    } else if (action === "DELETE") {
      deleteDialog.open();
    } else if (action === "RELEASE") {
      releaseDialog.open();
    } else if (action === "REVERT_RELEASE") {
      revertRelease();
    } else if (action === "APPROVAL") {
      approvalDialog.open();
    } else if (action === "REVOKE_APPROVAL") {
      revokeDialog.open();
    } else if (action === "DOWNLOAD") {
      // handleDownload();
      downloadDialog.open();
    }
  };

  return (
    <>
      <ActionMenu
        id={id}
        items={generateDropdownActions(version, handleMenuClick)}
      />
      <ReanalyzeDialog
        fileName={
          showReanalyzeVersion
            ? version.software_info.product
            : version.package_name
        }
        isOpen={reanalyzeDialog.isOpen}
        onClose={reanalyzeDialog.close}
        onConfirm={reanalyzeVersion}
      />
      <PortalDialog
        title="Delete version?"
        confirmText="Delete"
        onCancel={deleteDialog.close}
        onClose={deleteDialog.close}
        isOpen={deleteDialog.isOpen}
        onConfirm={() => deleteVersion()}
      >
        {isDestructiveCatalogAction && (
          <Dialog.Banner
            title="Package will be removed from catalog"
            type="warning"
          >
            This is the only approved version in the package. Deleting it will
            also remove the package from the catalog.
          </Dialog.Banner>
        )}
        <div className={styles.content}>
          Do you want to delete version
          <LongTextLabel
            label={version.software_info.version}
            className={styles["delete-version"]}
          />
          ?
        </div>
      </PortalDialog>
      <ReleaseDialog
        onClose={releaseDialog.close}
        isOpen={releaseDialog.isOpen}
        version={version}
        editVersion={editVersion}
      />
      <ApprovalDialog
        onClose={approvalDialog.close}
        isOpen={approvalDialog.isOpen}
        subjectId={version.version_id}
        editApproval={editApproval}
      />
      <RevokeDialog
        onClose={revokeDialog.close}
        isOpen={revokeDialog.isOpen}
        subjectId={version.version_id}
        editApproval={editApproval}
        warning={
          isDestructiveCatalogAction
            ? {
                title: "Package will be removed from catalog",
                body: "This is the only approved version in the package. Revoking it will also remove the package from the catalog.",
              }
            : undefined
        }
      />
      <DownloadDialog
        onClose={downloadDialog.close}
        isOpen={downloadDialog.isOpen}
        fileID={version.version_id}
        fileType="VERSION"
      />
    </>
  );
};

export default VersionActions;
